import { useEffect, useRef, useState } from 'react';
import type { NextPage } from 'next';
import cn from 'classnames';
import Swiper from 'swiper';

import { Container } from '@dansk-metal/ui';
import { dayJS } from '@dansk-metal/utils/date';

import { CalendarItem } from '@web/components/calendar-item/calendar-item';
import { HeroTitle } from '@web/components/hero-block/hero-title/hero-title';
import { ThemedSkeleton } from '@web/components/themed-skeleton/themed-skeleton';

import TemplateLayout from '@web/layouts/template/template';

import { useAuthenticatedCRMRequestConfig } from '@web/services/crm-api/hooks';
import { useGetMemberById, useGetMemberCalendarItemByFilter } from '@web/services/crm-api/rest';
import { HeroAlignment } from '@web/services/umbraco/types/basic/Alignment';
import { BackgroundColor } from '@web/services/umbraco/types/basic/Color';
import { UmbracoPage } from '@web/services/umbraco/types/documents/UmbracoDocument';

import styles from './calendar-overview-page.module.scss';

export const calendarOverviewPageContentAlias = 'calendarOverviewPage' as const;

export type CalendarOverviewPageData = UmbracoPage<
	typeof calendarOverviewPageContentAlias,
	{
		hero: {
			title: string;
			subtitle?: string;
		};
	}
>;

const CalendarOverviewPageTemplate: NextPage<{ pageData: CalendarOverviewPageData }> = ({ pageData }) => {
	const { hero } = pageData.properties;
	const [activeTab, setActiveTab] = useState(0);
	const tabListRef = useRef<HTMLDivElement>(null);
	const tabListHeader = ['Kommende', 'Tidligere'];
	const { memberId, requestConfig } = useAuthenticatedCRMRequestConfig();
	const { data: member, isLoading: isMemberLoading } = useGetMemberById(memberId, { request: requestConfig });
	const [currentTime] = useState(dayJS().startOf('day').toISOString());

	const { data: upcomingCalendarItemsData, isLoading: upcomingEventsLoading } = useGetMemberCalendarItemByFilter(memberId, {
		pageSize: 250,
		start: currentTime,
	}, {
		query: {
			enabled: member?.flags?.isUnemployed || false,
		},
		request: requestConfig,
	});

	const { data: previousCalendarItemsData, isLoading: previousEventsLoading } = useGetMemberCalendarItemByFilter(memberId, {
		pageSize: 250,
		end: currentTime,
	}, {
		query: {
			enabled: member?.flags?.isUnemployed || false,
		},
		request: requestConfig,
	});

	const upcomingCalendarItems = upcomingCalendarItemsData?.items || [];
	const previousCalendarItems = previousCalendarItemsData?.items || [];
	const isLoading = upcomingEventsLoading || previousEventsLoading || isMemberLoading || !memberId;

	useEffect(() => {
		const tabList = tabListRef?.current;
		if (!tabList) {
			return;
		}

		const swiper = new Swiper(tabList, {
			slidesPerView: 'auto',
			spaceBetween: 24,
			freeMode: true,
			grabCursor: true,
			allowTouchMove: true,
		});

		swiper.on('slideChange', () => {
			setActiveTab(swiper.realIndex);
		});

		return () => {
			swiper.destroy(true, true);
		};
	}, []);

	const RenderEvents = ({ events }) => {
		return (
			<>
				{isLoading ? (
					<ThemedSkeleton
						className={styles.skeleton_theme_event}
						containerClassName={styles.skeleton_theme_container}
						count={20}
						inline
					/>
				) : (
					<>
						{events.map((item) => (
							<div key={item.id}>
								<button className={styles.button}>
									<CalendarItem
										startTime={item.start}
										endTime={item.end}
										title={item.name || ''}
										location={item.location}
										href={`/medlem/booking/${item.id}`}
										className={styles.overview_page}
										dateClassName={styles.center_margin}
									/>
								</button>
							</div>
						))}
					</>
				)}
			</>
		);
	};

	return (
		<TemplateLayout>
			{!!hero.title && (
				<HeroTitle
					title={hero.title}
					subtitle={hero.subtitle}
					alignment={HeroAlignment.Left}
					background={BackgroundColor.White}
				/>
			)}
			<Container>
				<div className={styles.tab_list} ref={tabListRef}>
					<div className={cn('swiper-wrapper', styles.swiper)}>
						{tabListHeader.map((title, index) => (
							<button key={title} className={cn(styles.tab, 'swiper-slide', { [styles.tab__active]: index === activeTab })} onClick={() => setActiveTab(index)}>
								{title}
							</button>
						))}
					</div>
				</div>
			</Container>
			<div className={styles.appointments_container}>
				<Container className={styles.cards_container}>
					{activeTab === 0 ? (
						<>
							{upcomingCalendarItems.length === 0 && !isLoading ? (
								<div className={styles.empty_state_container}>
									<p>Du har ingen kommende aftaler lige nu - tjek igen senere.</p>
								</div>
							) : (
								<RenderEvents events={upcomingCalendarItems} />
							)}

						</>

					) : (
						<>
							{previousCalendarItems.length === 0 && !isLoading ? (
								<div className={styles.empty_state_container}>
									<p>Du har ingen tidligere opgaver lige nu - tjek igen senere.</p>
								</div>
							) : (
								<RenderEvents events={previousCalendarItems} />
							)}

						</>
					)}
				</Container>
			</div>
		</TemplateLayout>
	);
};

export default CalendarOverviewPageTemplate;
